import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError, auth, citizenship } from "@client/lib/api";
import { getE2EConfig, getE2ECurrentUser } from "@client/lib/e2e";

type CurrentUserPayload = {
  user: any;
  nation: any;
  citizenRecord: any;
  balance: any;
};

export function useCurrentUser(enabled = true) {
  const e2eConfig = getE2EConfig();
  const e2eAuthBypass = !!e2eConfig?.authBypass;

  return useQuery<CurrentUserPayload | null>({
    queryKey: ["current-user", e2eAuthBypass ? "e2e" : "live"],
    queryFn: async () => {
      if (e2eAuthBypass) {
        return getE2ECurrentUser();
      }

      try {
        const res = await auth.me();
        return res;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          return null;
        }
        throw err;
      }
    },
    enabled,
    retry: false,
    staleTime: e2eAuthBypass ? Infinity : 30_000,
  });
}

export function useJoinNation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => citizenship.join(code),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["current-user"] });
      qc.invalidateQueries({ queryKey: ["nations"] });
    },
  });
}

export function useStake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, amount }: { code: string; amount: string }) =>
      citizenship.stake(code, amount),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["current-user"] });
      qc.invalidateQueries({ queryKey: ["nations"] });
    },
  });
}

export function useUnstake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, amount }: { code: string; amount: string }) =>
      citizenship.unstake(code, amount),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["current-user"] });
    },
  });
}

export function useDefect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      oldCode,
      newCode,
    }: {
      oldCode: string;
      newCode: string;
    }) => citizenship.defect(oldCode, newCode),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["current-user"] });
      qc.invalidateQueries({ queryKey: ["nations"] });
    },
  });
}
